import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useCanvasSelection, useCanvasInteractions } from '@sketch/canvas'
import copy from 'copy-to-clipboard'

import {
  useResponsiveDropdown,
  Dropdown,
  Tooltip,
  SFSymbolLabel,
} from '@sketch/components'
import { isSecondaryClick } from '@sketch/utils'
import { useToast } from '@sketch/toasts'
import {
  useInspectorContext,
  useInspectorSelectedElement,
} from '../../hooks/inspector'
import { useSymbolMasterDocument } from '../../hooks/inspector/useSymbolMasterDocument'

import {
  SketchElement,
  isLayerElement,
  isFrameElement,
  isSectionElement,
  ElementType,
  SymbolMaster,
  isLayerWithSharedStyle,
  SketchFrameElement,
  isVariantSetElement,
  useCopyLayerCSS,
  ElementIcon,
} from '@sketch/inspector'

import { useSubSketchElementsTreeAtRelativePosition } from './useSubSketchElementsTreeAtRelativePosition'
import { useTargetElement } from './useTargetElement'
import { getAutoSelectableElements } from './getAutoSelectableElements'
import { useExportModal } from '../FrameDetailInspector/sections/Exports/ExportModal'
import {
  useCreateLayerLink,
  useShouldEnableCopyLinkToLayer,
} from '../../../../canvas/components/LayersTree/useCreateLayerLink'
import { getChildrenInLayerListOrder } from '../../../../canvas/components/LayersTree/treeUtils'

import { ReactComponent as CopyCSSIcon } from '@sketch/icons/img-markdown-code-inline-17'
import { ReactComponent as CopyLinkIcon } from '@sketch/icons/img-markdown-link-share-17'
import { ReactComponent as ExportIcon } from '@sketch/icons/img-arrow-down-circle-17'
import { ReactComponent as OpenFrameIcon } from '@sketch/icons/frame-17'
import { ReactComponent as SectionIcon } from '@sketch/icons/section-17'
import { ReactComponent as VariantSetIcon } from '@sketch/icons/variants-17'
import { ReactComponent as SymbolIcon } from '@sketch/icons/symbol-17'
import { ReactComponent as CursorIcon } from '@sketch/icons/cursor-17'

import * as S from './FrameDetailInspectorContextMenu.styles'
import { Coordinates } from '../../../../annotations/types'
import { routes } from '@sketch/modules-common'
import { useNavigateToFrame } from '../../../../canvas/hooks/useNavigateToFrame'
import { useNavigateToSection } from '../../../../canvas/hooks/useNavigateToSection'
import { ErrorHandler } from '@sketch/tracing'

import { useVersioning } from '../../../../versioning'
import {
  HiddenLayerIcon,
  LockedLayerIcon,
} from '../../../../canvas/components/LayersTree/LayersTreeItem.styles'

type FrameDetailInspectorContextMenuProps = {
  className?: string
  /**
   * Menu position, relative to the artboard element.
   */
  contextMenuPosition: Coordinates | null
  /**
   * Set when the right click landed on a frame/section title overlay, to
   * that entity's UUID, so we can target it directly instead of hit testing
   * the point - see WEB-3882.
   */
  headerEntityUUID?: string | null
  onCloseMenu: () => void
}

/**
 * The context menu opening when right clicking on the artboard canvas when
 * using the inspector.
 */
export function FrameDetailInspectorContextMenu({
  className,
  contextMenuPosition,
  headerEntityUUID = null,
  onCloseMenu,
}: FrameDetailInspectorContextMenuProps) {
  const { setHoveredElement, sketchSceneRootElement } = useInspectorContext()
  const selectedElement = useInspectorSelectedElement()
  const { selectElement: setSelectedElement } = useCanvasSelection()

  const subSketchSceneRootElement = useSubSketchElementsTreeAtRelativePosition(
    contextMenuPosition,
    headerEntityUUID
  )

  const { setEnabled } = useCanvasInteractions()

  // Handle escape key to close the context menu
  useEffect(() => {
    if (!contextMenuPosition) {
      return
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onCloseMenu()
      }
    }

    // Use capture true to make sure we catch the event before other potential listeners
    window.addEventListener('keydown', handleEscapeKey, { capture: true })
    return () => {
      window.removeEventListener('keydown', handleEscapeKey, { capture: true })
    }
  }, [contextMenuPosition, onCloseMenu])

  // Close canvas context menu when layer tree context menu opens
  useEffect(() => {
    const handleCloseMenu = () => {
      if (contextMenuPosition) {
        onCloseMenu()
      }
    }

    window.addEventListener('layer-tree-context-menu-opened', handleCloseMenu)
    return () => {
      window.removeEventListener(
        'layer-tree-context-menu-opened',
        handleCloseMenu
      )
    }
  }, [contextMenuPosition, onCloseMenu])

  // Dispatch event when canvas context menu opens
  useEffect(() => {
    if (contextMenuPosition) {
      window.dispatchEvent(new CustomEvent('canvas-context-menu-opened'))
    }
  }, [contextMenuPosition])

  // Disable canvas interactions when context menu is open
  useEffect(() => {
    if (contextMenuPosition) {
      setEnabled(false)
      return () => {
        setEnabled(true)
      }
    }
  }, [contextMenuPosition, setEnabled])

  const elementsList = useMemo(() => {
    if (!subSketchSceneRootElement) {
      return []
    }

    /**
     * Only include the root element in the context menu if it's a frame.
     * For frames (artboards), we want to show the frame itself so users can select it.
     * For pages, we skip the root element and show only its children.
     *
     * We compare the UUIDs because the "subSketchSceneRootElement" contains a trimmed version of the
     * "sketchSceneRootElement"
     */
    if (
      sketchSceneRootElement?.elementUUID ===
      subSketchSceneRootElement?.elementUUID
    ) {
      // If the root element is a frame, include it in the context menu
      if (isFrameElement(subSketchSceneRootElement)) {
        return [subSketchSceneRootElement]
      }
      // Otherwise (for pages), show only the children
      return subSketchSceneRootElement.children
    }

    return [subSketchSceneRootElement]
  }, [subSketchSceneRootElement, sketchSceneRootElement])

  // Get the target element to apply the actions on. Normally this drills
  // down to the deepest element under the clicked point (see
  // useTargetElement), but a title click unambiguously identifies its own
  // frame/section, so use it directly rather than drilling into its content.
  const pointTargetElement = useTargetElement(elementsList, selectedElement)
  const autoSelectableElementsList = useMemo(
    () => getAutoSelectableElements(elementsList),
    [elementsList]
  )
  const pointAutoSelectedTargetElement = useTargetElement(
    autoSelectableElementsList,
    selectedElement
  )

  const targetElement = headerEntityUUID
    ? subSketchSceneRootElement
    : pointTargetElement
  const autoSelectedTargetElement = headerEntityUUID
    ? subSketchSceneRootElement
    : pointAutoSelectedTargetElement

  useEffect(() => {
    if (elementsList.length === 0 && contextMenuPosition) {
      // If the user right clicked to open menu but we realize that there is nothing
      // to show where the user clicked, reset the "open" position by closing the menu.
      onCloseMenu()
    }
  }, [contextMenuPosition, elementsList.length, onCloseMenu])

  // Auto select the target element when opening the menu
  // It could be that the target is already the selected element
  // in which case it doesn't change anything.
  useEffect(() => {
    if (autoSelectedTargetElement) {
      // When there is an element already selected, the hover state can bring confusion
      // so we reset it. See https://linear.app/sketch/issue/WEB-2883/selecting-a-background-layer-will-keep-selecting-it-if-you-try-to
      setHoveredElement(null)
      setSelectedElement(autoSelectedTargetElement)
    }
  }, [autoSelectedTargetElement, setSelectedElement, setHoveredElement])

  const handleClickOutside = useCallback(
    (e: MouseEvent | TouchEvent) => {
      // Ignore right-click events - they should be a no-op
      if (e instanceof MouseEvent && isSecondaryClick(e)) {
        return
      }
      onCloseMenu()
    },
    [onCloseMenu]
  )

  if (
    !contextMenuPosition ||
    !subSketchSceneRootElement ||
    // We are already closing the menu in the use effect above but just in case,
    // let's avoid render the menu at all.
    elementsList.length === 0 ||
    !targetElement
  ) {
    return null
  }

  const isSymbol = targetElement.type === 'symbolMaster'
  const isSymbolInstance = targetElement.type === 'symbolInstance'
  const isVariantSet = isVariantSetElement(targetElement)

  const hasSharedStyle =
    (isFrameElement(targetElement) || isLayerElement(targetElement)) &&
    isLayerWithSharedStyle(targetElement.style) &&
    Boolean(targetElement.style.sharedStyleReference)

  const frameElement: SketchFrameElement | undefined = isFrameElement(
    targetElement
  )
    ? targetElement
    : undefined

  const handleContextMenu = (e: React.MouseEvent) => {
    // Prevent right-click events from propagating to the canvas
    // which would trigger a new context menu to open
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <S.DropdownWrapper
      onClickOutside={handleClickOutside}
      placement="auto-start"
      style={{
        left: contextMenuPosition.x,
        top: contextMenuPosition.y,
      }}
      contentStyle={{ maxWidth: 220 }}
      className={className}
      visible={true}
      maxWidth="100%"
      usePortal={true}
      toggleDropdown={onCloseMenu}
    >
      <div onContextMenu={handleContextMenu}>
        <S.StyledDropdownHeader>
          <ElementIcon
            elementType={targetElement.type}
            isLayerMasked={
              isLayerElement(targetElement)
                ? targetElement.isLayerMasked
                : false
            }
            isLayerExportable={targetElement.isExportable}
            isVariantSet={isVariantSet}
            isComponent={
              isSymbol || hasSharedStyle || isSymbolInstance || isVariantSet
            }
            isStack={!!frameElement?.stack}
            stackDirection={frameElement?.stack?.direction}
          />
          <S.DropdownHeaderText>
            <SFSymbolLabel text={targetElement.name} />
          </S.DropdownHeaderText>
        </S.StyledDropdownHeader>
        <Dropdown.Divider />
        <SelectLayerSubmenu
          elementsList={elementsList}
          selectedLayerIdentifier={selectedElement?.identifier}
          onSelect={(sketchElement: SketchElement) => {
            setSelectedElement(sketchElement)
            onCloseMenu()
          }}
          onHoverMenuItem={setHoveredElement}
        />

        <CanvasContextMenuActions
          sketchElement={targetElement}
          onCloseMenu={onCloseMenu}
          sketchSceneRootElement={sketchSceneRootElement}
        />
      </div>
    </S.DropdownWrapper>
  )
}

const SUBMENU_DROPDOWN_OFFSET: [number, number] = [0, -2]

type SelectLayerSubmenuProps = {
  elementsList: SketchElement[]
  selectedLayerIdentifier: bigint | undefined
  onSelect: (sketchElement: SketchElement) => void
  onHoverMenuItem: (sketchElement: SketchElement | null) => void
}

/**
 * A sub-menu that shows the list of layers under the cursor so
 * the user can select one of them more precisely.
 */
const SelectLayerDropdownContent: React.FC<{
  elementsList: SketchElement[]
  selectedLayerIdentifier: bigint | undefined
  onSelect: (sketchElement: SketchElement) => void
  onHoverMenuItem: (sketchElement: SketchElement | null) => void
}> = ({ elementsList, selectedLayerIdentifier, onSelect, onHoverMenuItem }) => {
  const handleContextMenu = (e: React.MouseEvent) => {
    // Prevent right-click events from propagating to the canvas
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <S.ContextMenuNav onContextMenu={handleContextMenu}>
      <SelectLayerList
        elementsList={elementsList}
        level={0}
        selectedLayerIdentifier={selectedLayerIdentifier}
        onSelect={onSelect}
        onHoverMenuItem={onHoverMenuItem}
      />
    </S.ContextMenuNav>
  )
}

type SelectLayerItemContentProps = {
  icon?: React.ReactNode
  rightIcon?: React.ReactNode
  children: React.ReactNode
}

function SelectLayerItemContent({
  icon,
  rightIcon,
  children,
}: SelectLayerItemContentProps) {
  return (
    <S.SelectLayerItemContentWrapper>
      {icon}
      {children}
      {rightIcon && <S.RightIconSlot>{rightIcon}</S.RightIconSlot>}
    </S.SelectLayerItemContentWrapper>
  )
}

function SelectLayerSubmenu({
  elementsList,
  selectedLayerIdentifier,
  onSelect,
  onHoverMenuItem,
}: SelectLayerSubmenuProps) {
  const dropdownProps = useMemo(
    () => ({
      elementsList,
      selectedLayerIdentifier,
      onSelect,
      onHoverMenuItem,
    }),
    [elementsList, selectedLayerIdentifier, onSelect, onHoverMenuItem]
  )

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: SelectLayerDropdownContent,
    dropdownProps,
    placement: 'right-start',
    showOnHover: { openDelay: 300 },
    usePortal: true,
    offset: SUBMENU_DROPDOWN_OFFSET,
    // If there is no space on the right, we try to flip to the left
    modifiers: [{ name: 'flip', enabled: true }],
    dropdownStyle: { minWidth: '150px', maxWidth: '400px' },
  })

  return (
    <>
      <S.SelectLayerItem icon={<CursorIcon />} {...buttonProps}>
        <SelectLayerItemContent rightIcon={<S.ArrowIcon />}>
          Select
        </SelectLayerItemContent>
      </S.SelectLayerItem>
      {content}
    </>
  )
}

type SelectLayerListProps = {
  elementsList: SketchElement[]
  level: number
  selectedLayerIdentifier: bigint | undefined
  onSelect: (sketchElement: SketchElement) => void
  onHoverMenuItem: (sketchElement: SketchElement | null) => void
}
/**
 * A recursive component that renders a tree of elements into ordered lists
 */
function SelectLayerList({
  elementsList,
  level,
  selectedLayerIdentifier,
  onSelect,
  onHoverMenuItem,
}: SelectLayerListProps) {
  const ref = useRef<HTMLOListElement>(null)

  /*
    We are overriding the native "mousemove" event listener
    so it doesn't cause conflicts with the "onMouseEnter" and "onMouseLeave"
    events on the <li> below
   */
  useEffect(() => {
    const element = ref.current

    if (element) {
      const handleMouseMove = (event: MouseEvent) => {
        event.stopPropagation()
      }

      element.addEventListener('mousemove', handleMouseMove)

      return () => {
        element.removeEventListener('mousemove', handleMouseMove)
      }
    }
  }, [])

  return (
    <ol key={`level${level}`} ref={ref}>
      {elementsList.map(sketchElement => {
        const isLayerMasked = isLayerElement(sketchElement)
          ? sketchElement.isLayerMasked
          : false

        const isSelfOrAncestorHidden =
          sketchElement.isHidden || sketchElement.isAncestorHidden
        const isSymbol = sketchElement.type === 'symbolMaster'
        const isSymbolInstance = sketchElement.type === 'symbolInstance'
        const isVariantSet = isVariantSetElement(sketchElement)

        const hasSharedStyle =
          (isFrameElement(sketchElement) || isLayerElement(sketchElement)) &&
          isLayerWithSharedStyle(sketchElement.style) &&
          Boolean(sketchElement.style.sharedStyleReference)

        const frameElement: SketchFrameElement | undefined = isFrameElement(
          sketchElement
        )
          ? sketchElement
          : undefined

        const isSelected = selectedLayerIdentifier === sketchElement.identifier

        return (
          <React.Fragment key={sketchElement.identifier.toString()}>
            <S.SelectLayerMenuItem
              $level={level}
              $isSymbol={isSymbol || isSymbolInstance || isVariantSet}
              $isSelfOrAncestorHidden={isSelfOrAncestorHidden}
              aria-selected={isSelected}
              onClick={e => {
                // Make sure the click doesn't propagate to the parent
                // since this component is rendered recursively.
                e.stopPropagation()
                onSelect(sketchElement)
              }}
              onMouseEnter={() => {
                onHoverMenuItem(sketchElement)
              }}
              onMouseLeave={() => {
                onHoverMenuItem(null)
              }}
              icon={
                <ElementIcon
                  elementType={sketchElement.type}
                  isLayerMasked={isLayerMasked}
                  isLayerExportable={sketchElement.isExportable}
                  isSelected={isSelected}
                  isVariantSet={isVariantSet}
                  isComponent={
                    isSymbol ||
                    hasSharedStyle ||
                    isSymbolInstance ||
                    isVariantSet
                  }
                  isStack={!!frameElement?.stack}
                  stackDirection={frameElement?.stack?.direction}
                />
              }
            >
              <S.SelectListLayerName $isSymbol={isSymbol || isVariantSet}>
                <SFSymbolLabel text={sketchElement.name} />
              </S.SelectListLayerName>
              {sketchElement.isHidden ? (
                <HiddenLayerIcon $isSelected={isSelected} />
              ) : sketchElement.isLocked ? (
                <LockedLayerIcon $isSelected={isSelected} />
              ) : null}
            </S.SelectLayerMenuItem>
            <SelectLayerList
              elementsList={getChildrenInLayerListOrder(sketchElement)}
              level={level + 1}
              selectedLayerIdentifier={selectedLayerIdentifier}
              onSelect={onSelect}
              onHoverMenuItem={onHoverMenuItem}
            />
          </React.Fragment>
        )
      })}
    </ol>
  )
}

type CanvasContextMenuActionsProps = {
  sketchElement: SketchElement
  onCloseMenu: () => void
  sketchSceneRootElement: SketchElement | null
}

type ForeignSymbolOpenActionProps = {
  symbolMaster: SymbolMaster
}

/**
 * Foreign symbols need to be resolved first before we can open them (we don't know the share id).
 * It can be that we end-up with a symbol the user doesn't have access to.
 */
function ForeignSymbolOpenAction({
  symbolMaster,
}: ForeignSymbolOpenActionProps) {
  const { frame, shareIdentifier, isLoading } =
    useSymbolMasterDocument(symbolMaster)

  const isSymbolAccessible = Boolean(frame && shareIdentifier)

  const handleClick = () => {
    if (!isSymbolAccessible) return

    const url =
      routes.FRAME.create({
        shareID: shareIdentifier!,
        frameUUID: frame!.uuid,
      }) + '#Inspect'

    window.open(url, '_blank')
  }

  if (isLoading) {
    return (
      <S.StyledDropdownItem disabled={true} icon={<S.LoadingSpinner />}>
        Open Symbol Source
      </S.StyledDropdownItem>
    )
  }

  if (isSymbolAccessible) {
    return (
      <S.StyledDropdownItem onClick={handleClick} icon={<SymbolIcon />}>
        Open Symbol Source
      </S.StyledDropdownItem>
    )
  }

  return (
    <div onClick={e => e.stopPropagation()}>
      <Tooltip
        placement="top"
        content="This Symbol belongs to a Library that is unavailable"
      >
        <S.StyledDropdownItem disabled={true} icon={<SymbolIcon />}>
          Open Symbol Source
        </S.StyledDropdownItem>
      </Tooltip>
    </div>
  )
}

function CanvasContextMenuActions({
  sketchElement,
  onCloseMenu,
  sketchSceneRootElement,
}: CanvasContextMenuActionsProps) {
  const { showToast } = useToast()
  const { openExportModal } = useExportModal()
  const { share, getPathname } = useVersioning()
  const navigateToFrame = useNavigateToFrame()
  const navigateToSection = useNavigateToSection()

  const { layerCSS, copyLayerCSS } = useCopyLayerCSS(sketchElement)
  const createLayerLink = useCreateLayerLink()

  const handleCopyCSSClick = () => {
    if (!copyLayerCSS()) return
    showToast('CSS copied')
    onCloseMenu()
  }

  const handleCopyLinkClick = () => {
    const url = createLayerLink(sketchElement.layerPathHash)
    if (url) {
      copy(url)
      showToast('Link copied')
    }
    onCloseMenu()
  }

  const handleExportAsClick = () => {
    openExportModal(sketchElement.identifier, sketchElement.name)
    onCloseMenu()
  }

  const { nearestOpenableFrame } = sketchElement

  let symbolMaster: SymbolMaster | undefined = undefined
  if (sketchElement.type === ElementType.SymbolInstance) {
    symbolMaster = sketchElement.symbolMaster
  } else if (isLayerElement(sketchElement)) {
    symbolMaster = sketchElement.symbolInstanceAncestorElement?.symbolMaster
  }

  // Check if we need symbol resolution (for library symbols)
  const needsSymbolResolution = Boolean(symbolMaster?.isForeign)

  const handleOpenFrameClick = () => {
    if (!nearestOpenableFrame) return

    if (
      nearestOpenableFrame.type === ElementType.Artboard ||
      nearestOpenableFrame.type === ElementType.SymbolMaster
    ) {
      // For artboards and symbol masters, use the regular navigation
      navigateToFrame(nearestOpenableFrame.targetUUID, 'contextMenuAction')
      onCloseMenu()
      return
    }

    if (needsSymbolResolution) {
      ErrorHandler.shouldNeverHappen(
        'Foreign symbol resolution should be handled by ForeignSymbolOpenAction component'
      )
      onCloseMenu()
      return
    }
    // From here we are expecting the nearest openable frame to be part of the document.

    // Symbol sources are opened in a new tab,
    const url = getPathname({
      routeKey: 'FRAME',
      routeParams: {
        shareID: share.identifier,
        frameUUID: nearestOpenableFrame.targetUUID,
      },
    })
    window.open(url, '_blank')
    onCloseMenu()
  }

  // Offer "Show Section" only on the Section itself - not on the frames and
  // layers nested inside it - so the action jumps into that Section's focused
  // view. A variant set is modelled as a Section too, so it gets the action as
  // well, labelled "Show Variant set". Hidden when the Section is already the
  // scene root (i.e. we are viewing it in focus). Undefined in FrameView, whose
  // frame-rooted scene has no Section element.
  const section = isSectionElement(sketchElement) ? sketchElement : undefined
  const isVariantSetSection = isVariantSetElement(sketchElement)
  const isSectionCurrentlyOpen =
    section &&
    sketchSceneRootElement &&
    section.elementUUID === sketchSceneRootElement.elementUUID

  const handleShowSectionClick = () => {
    if (!section) return
    navigateToSection(section.elementUUID, 'contextMenuAction')
    onCloseMenu()
  }

  const shouldEnableCopyLink = useShouldEnableCopyLinkToLayer()

  // When in the frameview, sketchSceneRootElement will be equal to the currently open frame.
  const isOpenableFrameCurrentlyOpen =
    nearestOpenableFrame &&
    sketchSceneRootElement &&
    nearestOpenableFrame.targetUUID === sketchSceneRootElement.elementUUID

  function getNearestOpenableFrameLabel() {
    if (!nearestOpenableFrame) return undefined

    if (nearestOpenableFrame.type === ElementType.Artboard) {
      return nearestOpenableFrame.isSelf ? 'Open Frame' : 'Open Top-Level Frame'
    } else if (nearestOpenableFrame.type === ElementType.SymbolMaster) {
      return nearestOpenableFrame.isSelf ? 'Open Symbol' : 'Open Parent Symbol'
    } else if (nearestOpenableFrame.type === ElementType.SymbolInstance) {
      return 'Open Symbol Source'
    }
  }

  return (
    <>
      {nearestOpenableFrame && !isOpenableFrameCurrentlyOpen && (
        <>
          {needsSymbolResolution && (
            <ForeignSymbolOpenAction symbolMaster={symbolMaster!} />
          )}
          {!needsSymbolResolution && (
            <S.StyledDropdownItem
              onClick={handleOpenFrameClick}
              icon={
                nearestOpenableFrame.type === ElementType.Artboard ? (
                  <OpenFrameIcon />
                ) : (
                  <SymbolIcon />
                )
              }
            >
              {getNearestOpenableFrameLabel()}
            </S.StyledDropdownItem>
          )}
        </>
      )}
      {section && !isSectionCurrentlyOpen && (
        <S.StyledDropdownItem
          onClick={handleShowSectionClick}
          icon={isVariantSetSection ? <VariantSetIcon /> : <SectionIcon />}
        >
          {isVariantSetSection ? 'Show Variant set' : 'Show Section'}
        </S.StyledDropdownItem>
      )}
      {shouldEnableCopyLink && (
        <S.StyledDropdownItem
          onClick={handleCopyLinkClick}
          icon={<CopyLinkIcon />}
        >
          Copy Link
        </S.StyledDropdownItem>
      )}
      {layerCSS.css.length > 0 && (
        <S.StyledDropdownItem
          icon={<CopyCSSIcon />}
          onClick={handleCopyCSSClick}
        >
          Copy CSS
        </S.StyledDropdownItem>
      )}
      <S.StyledDropdownItem onClick={handleExportAsClick} icon={<ExportIcon />}>
        Export as…
      </S.StyledDropdownItem>
    </>
  )
}
